import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Collapse,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { getPaginatedUsers } from '../../../services/api';
import AddSupervisorModal from './AddSupervisorModal';
import AssignTherapistsModal from './AssignTherapistsModal';
import EditSupervisorModal from './EditSupervisorModal';

function SupervisorsTab({ token, stats, onRefresh, defaultMaxTherapists = 10 }) {
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [assignTherapistsModal, setAssignTherapistsModal] = useState({ open: false, supervisor: null });
  const [editModal, setEditModal] = useState({ open: false, supervisor: null });

  useEffect(() => {
    loadSupervisors();
  }, [page, rowsPerPage, searchQuery, statusFilter, token]);

  const loadSupervisors = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        role: 'clinical_supervisor',
        sortBy: 'name',
        sortOrder: 'asc'
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const data = await getPaginatedUsers(params, token);
      setSupervisors(data.users || []);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading supervisors:', err);
      setError(err.message || 'Failed to load supervisors');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const toggleRowExpansion = (supervisorId) => {
    setExpandedRows(prev => ({
      ...prev,
      [supervisorId]: !prev[supervisorId]
    }));
  };

  const handleAddSuccess = () => {
    loadSupervisors();
    onRefresh();
    setAddModalOpen(false);
  };

  const handleAssignTherapistsSuccess = () => {
    loadSupervisors();
    onRefresh();
    setAssignTherapistsModal({ open: false, supervisor: null });
  };

  const handleEditSuccess = () => {
    loadSupervisors();
    onRefresh();
    setEditModal({ open: false, supervisor: null });
  };

  const getCapacityColor = (therapistCount, maxCapacity) => {
    const utilization = therapistCount / maxCapacity;
    if (utilization >= 1.0) return 'error';
    if (utilization >= 0.8) return 'warning';
    return 'success';
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search supervisors"
          placeholder="Name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Suspended">Suspended</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
        >
          Add Clinical Supervisor
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Specializations</TableCell>
              <TableCell>Therapists</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : supervisors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery || statusFilter ? 'No supervisors found matching filters' : 'No clinical supervisors yet'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              supervisors.map((supervisor) => (
                <React.Fragment key={supervisor._id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRowExpansion(supervisor._id)}>
                        {expandedRows[supervisor._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{supervisor.name}</TableCell>
                    <TableCell>{supervisor.email}</TableCell>
                    <TableCell>
                      {supervisor.supervisorInfo?.specializations?.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {supervisor.supervisorInfo.specializations.slice(0, 2).map((spec, idx) => (
                            <Chip key={idx} label={spec} size="small" />
                          ))}
                          {supervisor.supervisorInfo.specializations.length > 2 && (
                            <Chip
                              label={`+${supervisor.supervisorInfo.specializations.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">None</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${supervisor.supervisorInfo?.assignedTherapists?.length || 0}/${supervisor.supervisorInfo?.maxTherapistCapacity || 10}`}
                        size="small"
                        color={getCapacityColor(
                          supervisor.supervisorInfo?.assignedTherapists?.length || 0,
                          supervisor.supervisorInfo?.maxTherapistCapacity || 10
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={supervisor.status}
                        size="small"
                        color={supervisor.status === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => setEditModal({ open: true, supervisor })}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GroupIcon />}
                          onClick={() => setAssignTherapistsModal({ open: true, supervisor })}
                        >
                          Therapists
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedRows[supervisor._id] ? undefined : 0 }}>
                      <Collapse in={expandedRows[supervisor._id]} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Clinical Supervisor Details
                          </Typography>

                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Max Capacity</Typography>
                              <Typography variant="body2">
                                {supervisor.supervisorInfo?.maxTherapistCapacity || 10} therapists
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">Capacity Utilization</Typography>
                              <Typography variant="body2">
                                {Math.round(((supervisor.supervisorInfo?.assignedTherapists?.length || 0) / (supervisor.supervisorInfo?.maxTherapistCapacity || 10)) * 100)}%
                              </Typography>
                            </Box>

                            <Box sx={{ gridColumn: '1 / -1' }}>
                              <Typography variant="caption" color="text.secondary">Assigned Therapists</Typography>
                              {supervisor.supervisorInfo?.assignedTherapists?.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {supervisor.supervisorInfo.assignedTherapists.map((therapist) => (
                                    <Chip
                                      key={therapist._id || therapist}
                                      label={therapist.name || therapist}
                                      size="small"
                                      icon={<PersonIcon />}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">No therapists assigned</Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {/* Add Supervisor Modal */}
      <AddSupervisorModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        token={token}
        defaultMaxTherapists={defaultMaxTherapists}
      />

      {/* Assign Therapists Modal */}
      {assignTherapistsModal.supervisor && (
        <AssignTherapistsModal
          open={assignTherapistsModal.open}
          supervisor={assignTherapistsModal.supervisor}
          onClose={() => setAssignTherapistsModal({ open: false, supervisor: null })}
          onSuccess={handleAssignTherapistsSuccess}
          token={token}
        />
      )}

      {/* Edit Supervisor Modal */}
      {editModal.supervisor && (
        <EditSupervisorModal
          open={editModal.open}
          supervisor={editModal.supervisor}
          onClose={() => setEditModal({ open: false, supervisor: null })}
          onSuccess={handleEditSuccess}
          token={token}
        />
      )}
    </Box>
  );
}

export default SupervisorsTab;
